import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { User, TrendingUp, Activity, AlertTriangle, ShieldAlert, Rocket, ThumbsUp, Table as TableIcon, ChevronDown, ChevronUp, GraduationCap, Award, BookOpen, Scale, Wallet } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import HeaderActionsPortal from './HeaderActionsPortal';
import { ToggleGroup, CheckboxGroup, MultiSelectDropdown } from './TalentBankControls';
import { COLOR_PALETTE, GRADE_SCALE, gradeToValue, valueToGradeLabel, CORE_SUBJECT_KEYWORDS, computeActivityHours, oleBilingualLabel, ALL_NATURES_KEY } from '../talentBank';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

// One student's Talent Bank: academic record, OLE record, conduct. Identical for a teacher, the
// student and their guardian -- which is why it lives here once instead of being duplicated into
// both screens, where two copies of the same charts would drift apart.
//
// It knows nothing about roles. What it cannot know is who the caller may look at, or which query
// their security rules will accept, so the screen above it owns the selection and hands the results
// down:
//
//   sid, studentMeta   who this report is about
//   pickerSlot         the screen's own way of choosing that person -- a class filter and search for
//                      staff, a child dropdown for a guardian, nothing at all for a student
//   oleActivities      loaded by the screen: staff read ole_record, a family reads ole_participant
//   creditRecords      loaded by the screen, scoped the way its rule branch requires
//   creditBalances     { yearly: [{year, balance}], lifetime } -- staff take these from the
//                      aggregate document, a family derives them from the records above
//
// Assessment records are the exception: ass_raw_data is queried by SID for everybody, the same way,
// so the report loads those itself rather than making both screens repeat it.
const TalentBankReport = ({
  sid,
  studentMeta,
  pickerSlot,
  oleActivities,
  loadingOle,
  creditRecords,
  loadingCredit,
  creditBalances,
  // What to show before anybody is selected. Supplied by the screen because the honest wording
  // differs: staff have a search box to point at, a family has nothing to search.
  emptyState,
}) => {
  const { t } = useT();
  const { activeSchoolId, isMobile } = useOutletContext();

  // Named to match the code moved in below, which refers to the selected student throughout.
  const selectedSid = sid;
  const selectedMeta = studentMeta;

  const [activeTab, setActiveTab] = useState('academic');

  // --- OLE vocabulary, shared across students ---
  const [oleNatures, setOleNatures] = useState([]);
  const [oleRoles, setOleRoles] = useState([]);
  const [oleOrganizations, setOleOrganizations] = useState([]);
  const [selectedNatures, setSelectedNatures] = useState(new Set());
  const [expandedOleIds, setExpandedOleIds] = useState(new Set());

  const [subjectConfig, setSubjectConfig] = useState([]);
  const [assessConfig, setAssessConfig] = useState([]);
  const [fullMarkMap, setFullMarkMap] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);

  const subjectMap = useMemo(() => {
    const m = {};
    subjectConfig.forEach(s => { m[s.code] = { name: s.nameEn || s.name || s.code, seq: s.seq ?? 999 }; });
    return m;
  }, [subjectConfig]);

  const assessMap = useMemo(() => {
    const m = {};
    assessConfig.forEach(a => { m[a.code] = { name: a.nameEn || a.name || a.code, seq: a.seq ?? 999 }; });
    return m;
  }, [assessConfig]);

  useEffect(() => {
    if (!activeSchoolId) return;
    const fetchConfig = async () => {
      setConfigLoaded(false);
      try {
        const settingsSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
        if (settingsSnap.exists()) {
          const dropdowns = settingsSnap.data().dropdowns || {};
          setSubjectConfig(dropdowns.subjects || []);
          setAssessConfig(dropdowns.assessments || []);
        }
        const fmSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'subj_full_mark'));
        const fmMap = {};
        fmSnap.forEach(d => {
          const data = d.data();
          if (data.subjCode && data.fullMark !== undefined) fmMap[data.subjCode] = Number(data.fullMark);
        });
        setFullMarkMap(fmMap);
      } catch (e) {
        console.error('Error loading Talent Bank config', e);
      }
      setConfigLoaded(true);
    };
    fetchConfig();
  }, [activeSchoolId]);

  // --- OLE settings (natures / roles / organizations), shared across students ---
  useEffect(() => {
    if (!activeSchoolId) return;
    const fetchOleSettings = async () => {
      try {
        const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'ole_settings'));
        if (snap.exists()) {
          const data = snap.data();
          setOleNatures(Array.isArray(data.natures) ? data.natures : []);
          setOleRoles(Array.isArray(data.participantRoles) ? data.participantRoles : []);
          setOleOrganizations(Array.isArray(data.organizations) ? data.organizations : []);
        }
      } catch (e) {
        console.error('Error loading OLE settings', e);
      }
    };
    fetchOleSettings();
  }, [activeSchoolId]);

  // --- Student search (staff/teacher/admin view) ---

  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState(new Set());
  const [trendAssess, setTrendAssess] = useState(new Set());
  const [filterYears, setFilterYears] = useState(new Set());
  const [filterAssess, setFilterAssess] = useState(new Set());
  const [trendMetric, setTrendMetric] = useState('Mark');
  const [radarMetric, setRadarMetric] = useState('Mark');
  const [sortBy, setSortBy] = useState('Subject');

  useEffect(() => {
    if (!activeSchoolId || !configLoaded || !selectedSid) {
      setRecords([]);
      return;
    }
    const fetchRecords = async () => {
      setLoadingRecords(true);
      try {
        const q = query(collection(db, 'schools', activeSchoolId, 'ass_raw_data'), where('SID', '==', selectedSid));
        const snap = await getDocs(q);
        const rows = [];
        snap.forEach(d => {
          const r = d.data();
          const mark = r.Mark !== undefined && r.Mark !== '' ? Number(r.Mark) : null;
          const fullMark = fullMarkMap[r.CODE_ID] ?? 100;
          const normalized = mark !== null && !isNaN(mark) ? Math.min(100, (mark / fullMark) * 100) : null;
          const rank = r.OMF !== undefined && r.OMF !== '' ? Number(r.OMF) : null;
          rows.push({
            id: d.id,
            year: r.SCHYEAR,
            assessCode: r.TIMESEQ,
            assessName: assessMap[r.TIMESEQ]?.name || r.TIMESEQ,
            assessSeq: assessMap[r.TIMESEQ]?.seq ?? 999,
            subjectCode: r.CODE_ID,
            subjectName: subjectMap[r.CODE_ID]?.name || r.CODE_ID,
            mark,
            fullMark,
            normalized,
            grade: r.Grade || '',
            rank,
          });
        });

        // Default: latest school year, and the assessment with the highest seq available for it (e.g. Annual over Term).
        const years = [...new Set(rows.map(r => r.year))].sort().reverse();
        const latestYear = years[0];
        const assessInLatestYear = [...new Set(rows.filter(r => r.year === latestYear).map(r => r.assessCode))]
          .sort((a, b) => (assessMap[b]?.seq ?? 999) - (assessMap[a]?.seq ?? 999));

        setFilterYears(new Set(latestYear ? [latestYear] : []));
        setFilterAssess(new Set(assessInLatestYear.length ? [assessInLatestYear[0]] : []));
        setSelectedSubjects(new Set());
        setTrendAssess(new Set(rows.map(r => r.assessCode)));
        setRecords(rows);
      } catch (e) {
        console.error('Error loading assessment records', e);
      }
      setLoadingRecords(false);
    };
    fetchRecords();
  }, [activeSchoolId, configLoaded, selectedSid, fullMarkMap, subjectMap, assessMap]);

  const distinctYears = useMemo(() => [...new Set(records.map(r => r.year))].sort().reverse(), [records]);
  const distinctAssess = useMemo(
    () => [...new Set(records.map(r => r.assessCode))].sort((a, b) => (assessMap[a]?.seq ?? 999) - (assessMap[b]?.seq ?? 999)),
    [records, assessMap]
  );
  const distinctSubjects = useMemo(() => {
    const seen = new Map();
    records.forEach(r => { if (!seen.has(r.subjectCode)) seen.set(r.subjectCode, r.subjectName); });
    return [...seen.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [records]);

  const toggleSetItem = (setter, currentSet, item) => {
    const next = new Set(currentSet);
    if (next.has(item)) next.delete(item); else next.add(item);
    setter(next);
  };

  // --- Trend chart data (uses full history, filtered only by selected subjects) ---
  const trendData = useMemo(() => {
    if (selectedSubjects.size === 0 || trendAssess.size === 0) return [];
    const relevant = records.filter(r => selectedSubjects.has(r.subjectCode) && trendAssess.has(r.assessCode));
    const examKeys = [...new Set(relevant.map(r => `${r.year}__${r.assessCode}`))];
    examKeys.sort((a, b) => {
      const [ya, ta] = a.split('__');
      const [yb, tb] = b.split('__');
      if (ya !== yb) return ya.localeCompare(yb);
      return (assessMap[ta]?.seq ?? 999) - (assessMap[tb]?.seq ?? 999);
    });
    return examKeys.map(key => {
      const [year, assessCode] = key.split('__');
      const point = { label: `${year} ${assessMap[assessCode]?.name || assessCode}` };
      selectedSubjects.forEach(subCode => {
        const rec = relevant.find(r => r.year === year && r.assessCode === assessCode && r.subjectCode === subCode);
        if (!rec) { point[subCode] = null; return; }
        if (trendMetric === 'Mark') point[subCode] = rec.normalized;
        else if (trendMetric === 'Rank') point[subCode] = rec.rank;
        else point[subCode] = gradeToValue(rec.grade);
      });
      return point;
    });
  }, [records, selectedSubjects, trendAssess, trendMetric, assessMap]);

  // --- SWOT (based on the school year / assessment filter selection) ---
  const swot = useMemo(() => {
    const filtered = records.filter(r => filterYears.has(r.year) && filterAssess.has(r.assessCode));
    const s = [], w = [], o = [], t2 = [];
    filtered.forEach(r => {
      const norm = r.normalized ?? 0;
      const rank = r.rank ?? 999;
      const pct = t('talentBankReport.swotPercent', { subject: r.subjectName, percent: norm.toFixed(0) });
      if (norm >= 75 || rank <= 10) s.push(pct);
      if (norm < 50) w.push(pct);
      if (norm >= 45 && norm < 50) o.push(t('talentBankReport.swotBorderline', { subject: r.subjectName }));
      const lower = r.subjectName.toLowerCase();
      if (CORE_SUBJECT_KEYWORDS.some(k => lower.includes(k)) && norm < 40) {
        t2.push(t('talentBankReport.swotCoreRisk', { subject: r.subjectName }));
      }
    });
    return {
      strengths: [...new Set(s)], weaknesses: [...new Set(w)],
      opportunities: [...new Set(o)], threats: [...new Set(t2)],
    };
  }, [records, filterYears, filterAssess, t]);

  // --- Radar charts (one per selected year) ---
  const radarByYear = useMemo(() => {
    const filtered = records.filter(r => filterYears.has(r.year) && filterAssess.has(r.assessCode));
    const years = [...new Set(filtered.map(r => r.year))].sort().reverse();
    return years.map(year => {
      const yearRecords = filtered.filter(r => r.year === year);
      const subjects = [...new Set(yearRecords.map(r => r.subjectName))].sort();
      const assessCodes = [...new Set(yearRecords.map(r => r.assessCode))]
        .sort((a, b) => (assessMap[a]?.seq ?? 999) - (assessMap[b]?.seq ?? 999));
      const data = subjects.map(subName => {
        const row = { subject: subName };
        assessCodes.forEach(ac => {
          const rec = yearRecords.find(r => r.subjectName === subName && r.assessCode === ac);
          if (!rec) { row[ac] = null; return; }
          if (radarMetric === 'Mark') row[ac] = rec.normalized;
          else if (radarMetric === 'Rank') row[ac] = rec.rank !== null ? Math.max(0, (1 - rec.rank / 150) * 100) : null;
          else row[ac] = gradeToValue(rec.grade);
        });
        return row;
      });
      return { year, data, series: assessCodes.map(ac => ({ code: ac, name: assessMap[ac]?.name || ac })) };
    });
  }, [records, filterYears, filterAssess, radarMetric, assessMap]);

  // --- Raw data table ---
  const tableRows = useMemo(() => {
    const filtered = records.filter(r => filterYears.has(r.year) && filterAssess.has(r.assessCode));
    return [...filtered].sort((a, b) => {
      if (b.year !== a.year) return b.year.localeCompare(a.year);
      if (a.assessSeq !== b.assessSeq) return a.assessSeq - b.assessSeq;
      if (sortBy === 'Rank') return (a.rank ?? 999) - (b.rank ?? 999);
      if (sortBy === 'Grade') return (gradeToValue(b.grade) ?? -1) - (gradeToValue(a.grade) ?? -1);
      return a.subjectName.localeCompare(b.subjectName);
    });
  }, [records, filterYears, filterAssess, sortBy]);

  const trendYAxisProps = trendMetric === 'Mark'
    ? { domain: [0, 100] }
    : trendMetric === 'Rank'
      ? { reversed: true, domain: [1, 'dataMax'] }
      : { domain: [0, 8], tickFormatter: valueToGradeLabel };

  const trendTooltipFormatter = (value, name) => {
    if (value === null || value === undefined) return ['-', name];
    if (trendMetric === 'Mark') return [`${Number(value).toFixed(1)}%`, name];
    if (trendMetric === 'Grade') return [valueToGradeLabel(value), name];
    return [value, name];
  };

  // --- OLE Record: maps, nature multi-select, hours-by-year trend, and the activity list ---
  const oleNatureMap = useMemo(() => Object.fromEntries(oleNatures.map(n => [n.code, n])), [oleNatures]);
  const oleRoleMap = useMemo(() => Object.fromEntries(oleRoles.map(r => [r.code, r])), [oleRoles]);
  const oleOrgMap = useMemo(() => Object.fromEntries(oleOrganizations.map(o => [o.code, o])), [oleOrganizations]);

  const oleDistinctNatures = useMemo(() => {
    const seen = new Map();
    oleActivities.forEach(a => { if (a.nature && !seen.has(a.nature)) seen.set(a.nature, oleNatureMap[a.nature]?.nameEn || a.nature); });
    return [...seen.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [oleActivities, oleNatureMap]);

  const oleTrendData = useMemo(() => {
    const wantsAll = selectedNatures.has(ALL_NATURES_KEY);
    const byYear = {};
    oleActivities.forEach(a => {
      const yr = a.schoolYear || 'Unknown';
      const hrs = computeActivityHours(a.timeStart, a.timeEnd);
      byYear[yr] = byYear[yr] || {};
      // "All" totals every activity regardless of nature, independent of which individual natures are also selected.
      if (wantsAll) byYear[yr][ALL_NATURES_KEY] = (byYear[yr][ALL_NATURES_KEY] || 0) + hrs;
      if (a.nature && selectedNatures.has(a.nature)) byYear[yr][a.nature] = (byYear[yr][a.nature] || 0) + hrs;
    });
    return Object.keys(byYear).sort().map(yr => {
      const point = { label: yr };
      selectedNatures.forEach(code => { point[code] = byYear[yr][code] || 0; });
      return point;
    });
  }, [oleActivities, selectedNatures]);

  const oleGroups = useMemo(() => {
    const groups = {};
    oleActivities.forEach(a => {
      const yr = a.schoolYear || 'Unknown';
      (groups[yr] = groups[yr] || []).push(a);
    });
    return Object.keys(groups)
      .sort((a, b) => b.localeCompare(a))
      .map(yr => ({
        schoolYear: yr,
        items: [...groups[yr]].sort((a, b) => `${b.activityDate || ''} ${b.timeStart || ''}`.localeCompare(`${a.activityDate || ''} ${a.timeStart || ''}`)),
      }));
  }, [oleActivities]);

  const toggleOleExpand = (id) => {
    setExpandedOleIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // --- Behaviour: net balance by year/lifetime, and award/punishment summaries ---
  // Where these numbers came from is the screen's business: an aggregate document for staff, the
  // records themselves for a family. Both shapes are the same by the time they get here.
  const creditYearlyBalances = creditBalances?.yearly || [];
  const creditLifetimeBalance = creditBalances?.lifetime || 0;
  const summarizeByItem = (nature) => {
    const map = {};
    creditRecords.filter(r => r.nature === nature).forEach(r => {
      const key = r.itemCode || r.description || 'Unknown';
      if (!map[key]) map[key] = { key, description: r.description || r.itemCode || 'Unknown', count: 0, total: 0 };
      map[key].count += 1;
      map[key].total += Number(r.balanceChange) || 0;
    });
    return Object.values(map).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  };

  const awardSummary = useMemo(() => summarizeByItem('Award'), [creditRecords]);
  const punishmentSummary = useMemo(() => summarizeByItem('Punishment'), [creditRecords]);
  const awardTotal = useMemo(() => awardSummary.reduce((sum, i) => sum + i.total, 0), [awardSummary]);
  const punishmentTotal = useMemo(() => punishmentSummary.reduce((sum, i) => sum + i.total, 0), [punishmentSummary]);

  const hasStudent = !!(selectedSid && selectedMeta);

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs Bar */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          <button onClick={() => setActiveTab('academic')} className={`theme-tab ${activeTab === 'academic' ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
            <div className="theme-tab-icon"><GraduationCap size={18} /></div>
            {t('talentBankReport.tabs.academic')}
          </button>
          <button onClick={() => setActiveTab('ole')} className={`theme-tab ${activeTab === 'ole' ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
            <div className="theme-tab-icon"><Award size={18} /></div>
            {t('talentBankReport.tabs.ole')}
          </button>
          <button onClick={() => setActiveTab('behaviour')} className={`theme-tab ${activeTab === 'behaviour' ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
            <div className="theme-tab-icon"><Scale size={18} /></div>
            {t('talentBankReport.tabs.behaviour')}
          </button>
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {hasStudent && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', paddingRight: '0.75rem', borderRight: '1px solid var(--color-border)' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--color-bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--color-primary)', flexShrink: 0 }}>
                <User size={16} color="var(--color-text-muted)" />
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{selectedMeta.sid}</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{selectedMeta.currentClass || '-'} ({selectedMeta.classNumber || '-'})</span>
              <span style={{ fontSize: '0.9rem', fontWeight: '700', color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                {selectedMeta.name}{selectedMeta.chineseName ? ` (${selectedMeta.chineseName})` : ''}
              </span>
            </div>
          )}

          {pickerSlot}


          {activeTab === 'academic' && hasStudent && records.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0, paddingLeft: '0.75rem', borderLeft: '1px solid var(--color-border)' }}>
              <MultiSelectDropdown
                items={distinctYears}
                activeSet={filterYears}
                labelFn={y => y}
                onToggle={(y) => toggleSetItem(setFilterYears, filterYears, y)}
                placeholder={t('talentBankReport.yearFilter')}
                width="150px"
              />
              <MultiSelectDropdown
                items={distinctAssess}
                activeSet={filterAssess}
                labelFn={a => assessMap[a]?.name || a}
                onToggle={(a) => toggleSetItem(setFilterAssess, filterAssess, a)}
                placeholder={t('talentBankReport.assessmentFilter')}
                width="150px"
              />
            </div>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem' }}>
        {!hasStudent ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', textAlign: 'center', padding: '1.5rem' }}>
            {emptyState}
          </div>
        ) : activeTab === 'academic' ? (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {loadingRecords ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.loadingAcademic')}</div>
            ) : records.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>
                No assessment records found for this student.
              </div>
            ) : (
              <>
                {/* Trend Chart */}
                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', borderTop: '4px solid var(--color-primary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <TrendingUp size={22} color="var(--color-primary)" />
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.academicTrend')}</h3>
                    </div>
                    <ToggleGroup
                      value={trendMetric}
                      onChange={setTrendMetric}
                      options={[{ value: 'Mark', label: t('talentBankReport.metricPercent') }, { value: 'Rank', label: t('talentBankReport.metricRank') }, { value: 'Grade', label: t('talentBankReport.metricGrade') }]}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <small style={{ fontWeight: '700', textTransform: 'uppercase', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.selectSubjects')}</small>
                      <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8rem' }}>
                        <button onClick={() => setSelectedSubjects(new Set(distinctSubjects.map(s => s.code)))} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('talentBankReport.selectAll')}</button>
                        <button onClick={() => setSelectedSubjects(new Set())} style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('talentBankReport.clearAll')}</button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))', gap: '0.4rem', maxHeight: '140px', overflowY: 'auto', padding: '0.5rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                      {distinctSubjects.map(s => (
                        <label key={s.code} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedSubjects.has(s.code)}
                            onChange={() => toggleSetItem(setSelectedSubjects, selectedSubjects, s.code)}
                            style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.name}>{s.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {distinctAssess.length > 1 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <small style={{ fontWeight: '700', textTransform: 'uppercase', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.compareAssessments')}</small>
                        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8rem' }}>
                          <button onClick={() => setTrendAssess(new Set(distinctAssess))} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('talentBankReport.selectAll')}</button>
                          <button onClick={() => setTrendAssess(new Set())} style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('talentBankReport.clearAll')}</button>
                        </div>
                      </div>
                      <CheckboxGroup items={distinctAssess} activeSet={trendAssess} labelFn={a => assessMap[a]?.name || a} onToggle={(a) => toggleSetItem(setTrendAssess, trendAssess, a)} />
                    </div>
                  )}

                  <div style={{ height: '320px' }}>
                    {selectedSubjects.size === 0 ? (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>{t('talentBankReport.selectSubjectsToView')}</div>
                    ) : trendAssess.size === 0 ? (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>{t('talentBankReport.selectOneAssessment')}</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
                          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} {...trendYAxisProps} />
                          <Tooltip formatter={trendTooltipFormatter} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                          <Legend formatter={(value) => subjectMap[value]?.name || value} />
                          {[...selectedSubjects].map((subCode, i) => (
                            <Line
                              key={subCode}
                              type="monotone"
                              dataKey={subCode}
                              name={subjectMap[subCode]?.name || subCode}
                              stroke={COLOR_PALETTE[i % COLOR_PALETTE.length]}
                              strokeWidth={2.5}
                              dot={{ r: 3, strokeWidth: 2 }}
                              activeDot={{ r: 5 }}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* SWOT Analysis */}
                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    <Activity size={22} color="var(--color-text-main)" />
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.swotAnalysis')}</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--color-success-bg-soft)', borderLeft: '4px solid var(--color-success)', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: 'var(--color-success-text)', marginBottom: '0.5rem' }}><ThumbsUp size={16} /> {t('talentBankReport.strengths')}</div>
                      <ul style={{ paddingLeft: '1.25rem', margin: 0, fontSize: '0.875rem', color: 'var(--color-success-text)' }}>
                        {swot.strengths.length === 0 ? <li>-</li> : swot.strengths.map((x, i) => <li key={i} style={{ marginBottom: '4px' }}>{x}</li>)}
                      </ul>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--color-danger-bg)', borderLeft: '4px solid var(--color-danger)', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: 'var(--color-danger-text)', marginBottom: '0.5rem' }}><AlertTriangle size={16} /> {t('talentBankReport.weaknesses')}</div>
                      <ul style={{ paddingLeft: '1.25rem', margin: 0, fontSize: '0.875rem', color: 'var(--color-danger-text)' }}>
                        {swot.weaknesses.length === 0 ? <li>-</li> : swot.weaknesses.map((x, i) => <li key={i} style={{ marginBottom: '4px' }}>{x}</li>)}
                      </ul>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--color-warning-bg)', borderLeft: '4px solid var(--color-warning)', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: 'var(--color-warning-text)', marginBottom: '0.5rem' }}><Rocket size={16} /> {t('talentBankReport.opportunities')}</div>
                      <ul style={{ paddingLeft: '1.25rem', margin: 0, fontSize: '0.875rem', color: 'var(--color-warning-text)' }}>
                        {swot.opportunities.length === 0 ? <li>-</li> : swot.opportunities.map((x, i) => <li key={i} style={{ marginBottom: '4px' }}>{x}</li>)}
                      </ul>
                    </div>
                    <div style={{ padding: '1rem', backgroundColor: 'var(--color-info-bg)', borderLeft: '4px solid var(--color-info)', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: 'var(--color-info-text)', marginBottom: '0.5rem' }}><ShieldAlert size={16} /> {t('talentBankReport.threats')}</div>
                      <ul style={{ paddingLeft: '1.25rem', margin: 0, fontSize: '0.875rem', color: 'var(--color-info-text)' }}>
                        {swot.threats.length === 0 ? <li>-</li> : swot.threats.map((x, i) => <li key={i} style={{ marginBottom: '4px' }}>{x}</li>)}
                      </ul>
                    </div>
                  </div>
                  <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                    <strong>{t('talentBankReport.classificationCriteria')}</strong> {t('talentBankReport.criteriaDetail')}
                  </div>
                </div>

                {/* Detailed Analysis */}
                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <TableIcon size={22} color="var(--color-primary)" />
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.detailedAnalysis')}</h3>
                    </div>
                    <ToggleGroup
                      value={radarMetric}
                      onChange={setRadarMetric}
                      options={[{ value: 'Mark', label: t('talentBankReport.byPercent') }, { value: 'Rank', label: t('talentBankReport.byRank') }, { value: 'Grade', label: t('talentBankReport.byGrade') }]}
                    />
                  </div>

                  {radarByYear.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.noDataForFilters')}</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '1.5rem' }}>
                      {radarByYear.map(({ year, data, series }) => (
                        <div key={year} style={{ flex: '1 1 380px', height: '360px', backgroundColor: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)', padding: '1rem', border: '1px solid var(--color-border)' }}>
                          <div style={{ textAlign: 'center', fontSize: '0.9rem', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '0.5rem' }}>{year}</div>
                          <ResponsiveContainer width="100%" height="90%">
                            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
                              <PolarGrid stroke="var(--color-border)" />
                              <PolarAngleAxis dataKey="subject" tick={{ fill: 'var(--color-text-subtle)', fontSize: 11, fontWeight: 600 }} />
                              <PolarRadiusAxis angle={30} domain={radarMetric === 'Grade' ? [0, 8] : [0, 100]} tick={false} axisLine={false} />
                              {series.map((s, i) => (
                                <Radar key={s.code} name={s.name} dataKey={s.code} stroke={COLOR_PALETTE[i % COLOR_PALETTE.length]} fill={COLOR_PALETTE[i % COLOR_PALETTE.length]} fillOpacity={0.35} />
                              ))}
                              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                              <Tooltip formatter={(value) => radarMetric === 'Grade' ? valueToGradeLabel(value) : (value === null ? '-' : Number(value).toFixed(0))} />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <small style={{ fontWeight: '700', textTransform: 'uppercase', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.rawData')}</small>
                    <ToggleGroup
                      value={sortBy}
                      onChange={setSortBy}
                      options={[{ value: 'Subject', label: t('talentBankReport.sortBySubject') }, { value: 'Rank', label: t('talentBankReport.sortByRank') }, { value: 'Grade', label: t('talentBankReport.sortByGrade') }]}
                    />
                  </div>
                  <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', maxHeight: '400px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead style={{ position: 'sticky', top: 0 }}>
                        <tr style={{ backgroundColor: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--color-text-subtle)' }}>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('talentBankReport.year')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('talentBankReport.assessment')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('talentBankReport.subject')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('talentBankReport.mark')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('talentBankReport.grade')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('talentBankReport.rank')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.length === 0 ? (
                          <tr><td colSpan="6" style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.noRecordsForFilters')}</td></tr>
                        ) : tableRows.map((row, i) => {
                          const isFail = (row.normalized ?? 0) < 50;
                          return (
                            <tr key={row.id} style={{ borderBottom: i === tableRows.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                              <td style={{ padding: '0.75rem 1rem' }}>{row.year}</td>
                              <td style={{ padding: '0.75rem 1rem' }}>{row.assessName}</td>
                              <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: 'var(--color-text-main)' }}>{row.subjectName}</td>
                              <td style={{ padding: '0.75rem 1rem', color: isFail ? 'var(--color-danger)' : 'inherit', fontWeight: isFail ? '700' : '400' }}>{row.mark ?? '-'} / {row.fullMark}</td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <span style={{ padding: '0.15rem 0.5rem', backgroundColor: row.grade.includes('A') || row.grade.includes('5') ? 'var(--color-success-bg)' : 'var(--color-bg-subtle)', color: row.grade.includes('A') || row.grade.includes('5') ? 'var(--color-success-text)' : 'var(--color-text-subtle)', borderRadius: '9999px', fontWeight: '700', fontSize: '0.75rem' }}>
                                  {row.grade || '-'}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{row.rank ?? '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : activeTab === 'ole' ? (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {loadingOle ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.loadingOle')}</div>
            ) : oleActivities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>
                No OLE activities recorded for this student.
              </div>
            ) : (
              <>
                {/* OLE Participation Summary (hours by nature, by school year) */}
                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', borderTop: '4px solid var(--color-primary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <Award size={22} color="var(--color-primary)" />
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.oleSummary')}</h3>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <small style={{ fontWeight: '700', textTransform: 'uppercase', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.selectNatures')}</small>
                      <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.8rem' }}>
                        <button onClick={() => setSelectedNatures(new Set(oleDistinctNatures.map(n => n.code)))} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('talentBankReport.selectAll')}</button>
                        <button onClick={() => setSelectedNatures(new Set())} style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{t('talentBankReport.clearAll')}</button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))', gap: '0.4rem', maxHeight: '140px', overflowY: 'auto', padding: '0.5rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '700' }}>
                        <input
                          type="checkbox"
                          checked={selectedNatures.has(ALL_NATURES_KEY)}
                          onChange={() => toggleSetItem(setSelectedNatures, selectedNatures, ALL_NATURES_KEY)}
                          style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                        />
                        <span title={t('talentBankReport.allNaturesTitle')}>{t('talentBankReport.all')}</span>
                      </label>
                      {oleDistinctNatures.map(n => (
                        <label key={n.code} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedNatures.has(n.code)}
                            onChange={() => toggleSetItem(setSelectedNatures, selectedNatures, n.code)}
                            style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                          />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={n.name}>{n.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ height: '320px' }}>
                    {selectedNatures.size === 0 ? (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>{t('talentBankReport.selectNaturesToView')}</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={oleTrendData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
                          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} tickFormatter={(v) => `${v}h`} />
                          <Tooltip formatter={(value, name) => [t('talentBankReport.hoursSuffix', { hours: Number(value).toFixed(1) }), name]} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                          <Legend formatter={(value) => value === ALL_NATURES_KEY ? 'All' : (oleNatureMap[value]?.nameEn || value)} />
                          {[...selectedNatures].map((natCode, i) => (
                            <Line
                              key={natCode}
                              type="monotone"
                              dataKey={natCode}
                              name={natCode === ALL_NATURES_KEY ? 'All' : (oleNatureMap[natCode]?.nameEn || natCode)}
                              stroke={natCode === ALL_NATURES_KEY ? 'var(--color-text-main)' : COLOR_PALETTE[i % COLOR_PALETTE.length]}
                              strokeWidth={natCode === ALL_NATURES_KEY ? 3 : 2.5}
                              strokeDasharray={natCode === ALL_NATURES_KEY ? '6 3' : undefined}
                              dot={{ r: 3, strokeWidth: 2 }}
                              activeDot={{ r: 5 }}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* OLE Activity Details */}
                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                    <BookOpen size={22} color="var(--color-text-main)" />
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.oleActivityDetails')}</h3>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0 1.25rem', fontSize: '0.75rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>
                      <div style={{ width: '100px', flexShrink: 0 }}>{t('studentCredit.date')}</div>
                      <div style={{ flex: '1 1 240px', minWidth: 0 }}>{t('talentBankReport.activity')}</div>
                      <div style={{ width: '170px', flexShrink: 0 }}>{t('studentCredit.natureLabel')}</div>
                      <div style={{ width: '130px', flexShrink: 0 }}>{t('talentBankReport.role')}</div>
                      <div style={{ flex: '1 1 160px', minWidth: 0 }}>{t('talentBankReport.achievement')}</div>
                      <div style={{ width: '16px', flexShrink: 0 }} />
                    </div>

                    {oleGroups.map(group => (
                      <div key={group.schoolYear}>
                        <h4 style={{ fontSize: '1.05rem', fontWeight: '700', margin: '0 0 0.75rem' }}>{group.schoolYear}</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                          {group.items.map(a => {
                            const isExpanded = expandedOleIds.has(a.id);
                            const detail = a.participantDetails?.[selectedSid];
                            const natureInfo = oleNatureMap[a.nature];
                            const roleInfo = detail?.role ? oleRoleMap[detail.role] : null;
                            return (
                              <div key={a.id} style={{ backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
                                <div
                                  onClick={() => toggleOleExpand(a.id)}
                                  style={{ padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem' }}
                                >
                                  <div style={{ width: '100px', flexShrink: 0, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{a.activityDate || '-'}</div>
                                  <div style={{ flex: '1 1 240px', minWidth: 0, overflow: 'hidden' }}>
                                    <div style={{ fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.titleEn || '-'}</div>
                                    {a.titleZh && <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.titleZh}</div>}
                                  </div>
                                  <div style={{ width: '170px', flexShrink: 0, overflow: 'hidden' }}>
                                    <div style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{natureInfo?.nameEn || '-'}</div>
                                    {natureInfo?.nameZh && <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{natureInfo.nameZh}</div>}
                                  </div>
                                  <div style={{ width: '130px', flexShrink: 0, overflow: 'hidden' }}>
                                    <div style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleInfo?.nameEn || '-'}</div>
                                    {roleInfo?.nameZh && <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleInfo.nameZh}</div>}
                                  </div>
                                  <div style={{ flex: '1 1 160px', minWidth: 0, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail?.achievement || '-'}</div>
                                  <div style={{ width: '16px', flexShrink: 0, color: 'var(--color-text-muted)', display: 'flex' }}>
                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                  </div>
                                </div>
                                {isExpanded && (
                                  <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '1rem', rowGap: '0.5rem', marginTop: '1rem' }}>
                                      <div style={{ color: 'var(--color-text-muted)' }}>{t('talentBankReport.activityId')}</div>
                                      <div>{a.id}</div>
                                      <div style={{ color: 'var(--color-text-muted)' }}>{t('talentBankReport.dateTime')}</div>
                                      <div>{a.activityDate || '-'} {a.timeStart || '?'} - {a.timeEnd || '?'}</div>
                                      <div style={{ color: 'var(--color-text-muted)' }}>{t('talentBankReport.organization')}</div>
                                      <div>{oleBilingualLabel(oleOrgMap[a.organization]) || '-'}</div>
                                      <div style={{ color: 'var(--color-text-muted)' }}>{t('talentBankReport.venue')}</div>
                                      <div>{[a.venueEn, a.venueZh].filter(Boolean).join(' / ') || '-'}</div>
                                      <div style={{ color: 'var(--color-text-muted)' }}>{t('talentBankReport.staffInCharge')}</div>
                                      <div>{a.staffInCharge || '-'}</div>
                                      {a.accompanyStaff?.length > 0 && (
                                        <>
                                          <div style={{ color: 'var(--color-text-muted)' }}>{t('talentBankReport.accompanyingStaff')}</div>
                                          <div>{a.accompanyStaff.join(', ')}</div>
                                        </>
                                      )}
                                      {(a.descriptionEn || a.descriptionZh) && (
                                        <>
                                          <div style={{ color: 'var(--color-text-muted)' }}>{t('talentBankReport.descriptionLabel')}</div>
                                          <div>
                                            {a.descriptionEn}
                                            {a.descriptionEn && a.descriptionZh && ' / '}
                                            {a.descriptionZh}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {loadingCredit ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.loadingBehaviour')}</div>
            ) : creditRecords.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>
                No award/punishment records for this student.
              </div>
            ) : (
              <>
                {/* Net Balance by School Year */}
                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', borderTop: '4px solid var(--color-primary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <Wallet size={22} color="var(--color-primary)" />
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.netBalanceByYear')}</h3>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-bg-app)', border: '2px solid var(--color-primary)', minWidth: '110px' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: '600' }}>{t('studentCredit.lifetime')}</div>
                      <div style={{ fontWeight: '700', fontSize: '1.2rem', color: creditLifetimeBalance > 0 ? 'var(--color-success)' : creditLifetimeBalance < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                        {creditLifetimeBalance > 0 ? '+' : ''}{creditLifetimeBalance}
                      </div>
                    </div>
                    {creditYearlyBalances.map(({ year, balance }) => (
                      <div key={year} style={{ padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', minWidth: '110px' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: '600' }}>{year}</div>
                        <div style={{ fontWeight: '700', fontSize: '1.2rem', color: balance > 0 ? 'var(--color-success)' : balance < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                          {balance > 0 ? '+' : ''}{balance}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Award / Punishment Summary */}
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 320px', backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <ThumbsUp size={20} color="var(--color-success)" />
                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.awardSummary')}</h3>
                      </div>
                      <span style={{ fontWeight: '700', color: 'var(--color-success)' }}>{awardTotal > 0 ? '+' : ''}{awardTotal}</span>
                    </div>
                    <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                            <th style={{ padding: '0.6rem 0.9rem', fontWeight: '600' }}>{t('studentCredit.item')}</th>
                            <th style={{ padding: '0.6rem 0.9rem', fontWeight: '600' }}>{t('talentBankReport.count')}</th>
                            <th style={{ padding: '0.6rem 0.9rem', fontWeight: '600' }}>{t('studentCredit.balance')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {awardSummary.length === 0 ? (
                            <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.noAwardRecords')}</td></tr>
                          ) : awardSummary.map(item => (
                            <tr key={item.key} style={{ borderTop: '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                              <td style={{ padding: '0.6rem 0.9rem' }}>{item.description}</td>
                              <td style={{ padding: '0.6rem 0.9rem' }}>{item.count}</td>
                              <td style={{ padding: '0.6rem 0.9rem', fontWeight: '600', color: 'var(--color-success)' }}>+{item.total}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div style={{ flex: '1 1 320px', backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AlertTriangle size={20} color="var(--color-danger)" />
                        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.punishmentSummary')}</h3>
                      </div>
                      <span style={{ fontWeight: '700', color: 'var(--color-danger)' }}>{punishmentTotal}</span>
                    </div>
                    <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                            <th style={{ padding: '0.6rem 0.9rem', fontWeight: '600' }}>{t('studentCredit.item')}</th>
                            <th style={{ padding: '0.6rem 0.9rem', fontWeight: '600' }}>{t('talentBankReport.count')}</th>
                            <th style={{ padding: '0.6rem 0.9rem', fontWeight: '600' }}>{t('studentCredit.balance')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {punishmentSummary.length === 0 ? (
                            <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1rem', color: 'var(--color-text-muted)' }}>{t('talentBankReport.noPunishmentRecords')}</td></tr>
                          ) : punishmentSummary.map(item => (
                            <tr key={item.key} style={{ borderTop: '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                              <td style={{ padding: '0.6rem 0.9rem' }}>{item.description}</td>
                              <td style={{ padding: '0.6rem 0.9rem' }}>{item.count}</td>
                              <td style={{ padding: '0.6rem 0.9rem', fontWeight: '600', color: 'var(--color-danger)' }}>{item.total}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Record Details */}
                <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                    <TableIcon size={22} color="var(--color-text-main)" />
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>{t('talentBankReport.recordDetails')}</h3>
                  </div>
                  <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', maxHeight: '420px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead style={{ position: 'sticky', top: 0 }}>
                        <tr style={{ backgroundColor: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--color-text-subtle)' }}>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.date')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('common.schoolYear')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.item')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.natureLabel')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.balance')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.remarks')}</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.staff')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditRecords.map((r, i) => (
                          <tr key={r.id} style={{ borderBottom: i === creditRecords.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                            <td style={{ padding: '0.75rem 1rem' }}>{r.date || '-'}</td>
                            <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{r.schoolYear || '-'}</td>
                            <td style={{ padding: '0.75rem 1rem' }}>{r.description || '-'}</td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <span style={{ padding: '0.15rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: r.nature === 'Award' ? 'var(--color-success-light)' : 'var(--color-danger-light)', color: r.nature === 'Award' ? 'var(--color-success)' : 'var(--color-danger)' }}>
                                {r.nature ? t(`studentCredit.nature.${r.nature.toLowerCase()}`, {}, r.nature) : '-'}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: (r.balanceChange || 0) > 0 ? 'var(--color-success)' : (r.balanceChange || 0) < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                              {(r.balanceChange || 0) > 0 ? '+' : ''}{r.balanceChange || 0}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{r.remarks || '-'}</td>
                            <td style={{ padding: '0.75rem 1rem' }}>{r.staffInCharge || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentBankReport;
